import { useEffect, useCallback, useRef } from 'react';
import { useBlocker, useNavigate, Location } from 'react-router-dom';
import { showConfirmModal } from '../components/PopupModal';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

/**
 * 네비게이션 차단 Hook
 *
 * 저장되지 않은 변경사항이 있을 때 페이지 이동을 차단하고 확인 팝업을 표시합니다.
 * React Router v6.4+ 의 useBlocker를 사용하며, Context를 통해 프로그래매틱 네비게이션도 차단합니다.
 *
 * @param isModified - 수정 여부 (true일 때 차단)
 * @param returnPath - 확인 시 이동할 경로
 *
 * @example
 * ```tsx
 * function EditPage() {
 *   const [isModified, setIsModified] = useState(false);
 *
 *   // 네비게이션 가드 적용
 *   useNavigationBlocker(isModified, '/list-page');
 *
 *   return (
 *     <Form onValuesChange={() => setIsModified(true)}>
 *       // ...
 *     </Form>
 *   );
 * }
 * ```
 */
export function useNavigationBlocker(isModified: boolean, returnPath: string) {
  const navigate = useNavigate();
  const { registerBlocker, unregisterBlocker, isNavigating } = useNavigationBlockerContext();
  const blockerIdRef = useRef(`blocker-${Math.random()}`);

  // Context에 blocker 등록/해제
  useEffect(() => {
    const blockerId = blockerIdRef.current;
    registerBlocker(blockerId, isModified, returnPath);

    return () => {
      unregisterBlocker(blockerId);
    };
  }, [isModified, returnPath, registerBlocker, unregisterBlocker]);

  // useBlocker로 React Router 네비게이션 차단 (Link, 뒤로가기 등)
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
        // isModified가 true이고, 다른 경로로 이동하려고 할 때만 차단
        return isModified && currentLocation.pathname !== nextLocation.pathname;
      },
      [isModified]
    )
  );

  // blocker 상태가 변경될 때 팝업 표시
  useEffect(() => {
    if (blocker.state === 'blocked') {
      // 이미 네비게이션 진행 중이면 팝업을 띄우지 않고 바로 진행
      if (isNavigating()) {
        blocker.proceed();
        return;
      }

      // Context blocker를 먼저 해제
      unregisterBlocker(blockerIdRef.current);

      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          // 네비게이션 진행
          blocker.proceed();
        },
        onCancel: () => {
          // 네비게이션 취소 시 Context blocker 다시 등록
          registerBlocker(blockerIdRef.current, isModified, returnPath);
          blocker.reset();
        },
      });
    }
  }, [blocker, returnPath, navigate, unregisterBlocker, registerBlocker, isModified, isNavigating]);
}
